// Panel-view geometry & port grouping. A device's ports are grouped by prefix;
// each group is laid out left-to-right in a configurable order, with a per-group
// layout. The same geometry is used by the interactive React node and the
// SVG/PDF renderer so the views match exactly.
import { deviceLabel, isPassiveDevice } from './deviceIcons';

export const PANEL_LAYOUTS = [
  { value: 'single', labelKey: 'panel_layout_single' },
  { value: 'double', labelKey: 'panel_layout_double' },
  { value: 'alt-odd-top', labelKey: 'panel_layout_alt_odd' },
  { value: 'alt-even-top', labelKey: 'panel_layout_alt_even' },
  { value: 'both-sides', labelKey: 'panel_layout_both_sides' },
];

// Horizontal metrics are tuned so every port ANCHOR lands on an 8px sub-grid
// relative to the device origin (see computePanelLayout):
//   - port pitch = CELL_W + GAP_X = 24 (multiple of 8)
//   - first anchor base = PAD + LABEL_W + PREFIX_W + CELL_W/2 = 176 (multiple of 8)
//   - per-group advance = groupW + GROUP_GAP = 48 + 24*cols (multiple of 8)
// Combined with the detail page's 8px X-snap, this lets grid-snapped devices
// line their connected port columns up EXACTLY, so cables between them render
// as straight vertical lines (even for devices with several port groups).
// DO NOT change these without re-checking the 8px alignment above.
export const P = {
  LABEL_W: 121, // fixed-width left info column (device name + IP), keeps ports clear of the label
  PREFIX_W: 34, // per-group left column holding the port prefix (replaces the old direction arrow)
  CELL_W: 22,
  CELL_H: 18,
  GAP_X: 2,
  ROW_GAP: 18,
  GROUP_GAP: 10,
  PAD: 10,
  TOP_PAD: 14, // top gap above the first port row (room for the connector stub)
  ICON: 18,
  NAME_FS: 11.5,
  NAME_LH: 13,
  IP_LH: 13,
  NAME_CHARS: 17,
  NAME_MAX_LINES: 3,
};

export function parsePortName(name) {
  const m = /^(.*?)(\d+)$/.exec(name || '');
  if (m) return { prefix: m[1], num: parseInt(m[2], 10) };
  return { prefix: name || '', num: null };
}

// Word-wrap a string into up to maxLines lines of at most maxChars each.
// Breaks on spaces and (preferably) after hyphens; hard-splits over-long atoms;
// adds an ellipsis if truncated.
export function wrapText(str, maxChars, maxLines) {
  const s = String(str == null ? '' : str).trim().replace(/\s+/g, ' ');
  if (!s) return [];
  // Atoms: words split by spaces; each word further split AFTER hyphens so a long
  // hyphenated name (e.g. "Distribution-Switch-EG-01") breaks at hyphen boundaries.
  const atoms = [];
  s.split(' ').forEach((word, wi) => {
    word
      .split(/(?<=-)/)
      .filter(Boolean)
      .forEach((p, pi) => atoms.push({ text: p, spaceBefore: wi > 0 && pi === 0 }));
  });
  const lines = [];
  let cur = '';
  for (const a of atoms) {
    let t = a.text;
    const sep = a.spaceBefore && cur ? ' ' : '';
    if (t.length > maxChars) {
      if (cur) {
        lines.push(cur);
        cur = '';
      }
      while (t.length > maxChars) {
        lines.push(t.slice(0, maxChars));
        t = t.slice(maxChars);
      }
      cur = t;
      continue;
    }
    if (!cur) cur = t;
    else if ((cur + sep + t).length <= maxChars) cur += sep + t;
    else {
      lines.push(cur);
      cur = t;
    }
  }
  if (cur) lines.push(cur);
  if (lines.length <= maxLines) return lines;
  const out = lines.slice(0, maxLines);
  let last = out[maxLines - 1];
  last = (last.length > maxChars - 1 ? last.slice(0, maxChars - 1) : last).replace(/\s+$/, '') + '\u2026';
  out[maxLines - 1] = last;
  return out;
}

// Ordered, prefix-grouped ports merged with the device's stored panel config.
export function getPanelGroups(node) {
  const ports = node?.data?.ports || [];
  // Passive devices (patch panels) default to the single-row, both-sides layout
  // so their connections dock to the nearest (left/right) side automatically.
  const defaultLayout = isPassiveDevice(node?.data?.deviceType) ? 'both-sides' : 'single';
  const byPrefix = new Map();
  ports.forEach((p) => {
    const { prefix, num } = parsePortName(p.name);
    if (!byPrefix.has(prefix)) byPrefix.set(prefix, []);
    byPrefix.get(prefix).push({ id: p.id, name: p.name, num });
  });
  for (const arr of byPrefix.values()) {
    arr.sort((a, b) => {
      if (a.num == null && b.num == null) return a.name.localeCompare(b.name);
      if (a.num == null) return 1;
      if (b.num == null) return -1;
      return a.num - b.num;
    });
  }
  const config = (node?.data?.panel?.groups || []).filter((g) => byPrefix.has(g.prefix));
  const order = [];
  const layoutOf = {};
  config.forEach((g) => {
    if (!order.includes(g.prefix)) {
      order.push(g.prefix);
      layoutOf[g.prefix] = g.layout || defaultLayout;
    }
  });
  for (const pfx of byPrefix.keys()) if (!order.includes(pfx)) order.push(pfx);
  return order.map((pfx) => ({ prefix: pfx, layout: layoutOf[pfx] || defaultLayout, ports: byPrefix.get(pfx) || [] }));
}

// Returns the normalized panel config to persist (keeps current order + layouts).
export function normalizePanelConfig(node) {
  return { groups: getPanelGroups(node).map((g) => ({ prefix: g.prefix, layout: g.layout })) };
}

function buildGrid(ports, layout) {
  const n = ports.length;
  if (layout === 'double') {
    const half = Math.ceil(n / 2);
    const cells = [];
    for (let c = 0; c < half; c++) {
      if (ports[c]) cells.push({ port: ports[c], r: 0, c });
      if (ports[half + c]) cells.push({ port: ports[half + c], r: 1, c });
    }
    return { cols: half, rows: 2, cells };
  }
  if (layout === 'alt-odd-top' || layout === 'alt-even-top') {
    const cols = Math.ceil(n / 2);
    const cells = [];
    for (let c = 0; c < cols; c++) {
      const a = ports[2 * c]; // lower number of the pair
      const b = ports[2 * c + 1]; // higher number of the pair
      if (layout === 'alt-odd-top') {
        if (a) cells.push({ port: a, r: 0, c }); // odd (port1) on top
        if (b) cells.push({ port: b, r: 1, c });
      } else {
        if (b) cells.push({ port: b, r: 0, c }); // even on top -> port1 bottom-left
        if (a) cells.push({ port: a, r: 1, c });
      }
    }
    return { cols, rows: 2, cells };
  }
  return { cols: n, rows: 1, cells: ports.map((p, c) => ({ port: p, r: 0, c })) };
}

// Full layout: device box dimensions, group/cell positions, and port anchors
// (where connections attach, at the box top/bottom edge above/below the column).
// A fixed-width left column holds the device name (wrapping) + IP; port groups
// begin to the right of it so the label is never overdrawn.
// opts.sideHints: { [prefix]: 'top' | 'bottom' } — for SINGLE-row groups only,
// forces the connection exit side (used so single-row groups face the majority
// of their connection peers above/below). Two-row layouts keep their split.
export function computePanelLayout(node, opts = {}) {
  const sideHints = opts.sideHints || {};
  const groups = getPanelGroups(node).map((g) => ({ ...g, grid: buildGrid(g.ports, g.layout) }));
  const maxRows = groups.reduce((m, g) => Math.max(m, g.grid.rows), 1);
  const cellsTop = P.TOP_PAD;
  const topRowY = cellsTop;
  const bottomRowY = topRowY + P.CELL_H + P.ROW_GAP;
  const portAreaH = (maxRows === 2 ? bottomRowY + P.CELL_H : topRowY + P.CELL_H) + P.PAD;

  // Left info column height (icon + wrapped name + optional IP). Uses the 'de'
  // label fallback for unnamed devices so the height stays deterministic.
  const nameForHeight = node?.data?.label || deviceLabel(node?.data?.deviceType, 'de');
  const nameLines = Math.max(1, wrapText(nameForHeight, P.NAME_CHARS, P.NAME_MAX_LINES).length);
  const hasIp = !!(node?.data?.ip && String(node.data.ip).trim());
  const leftColH = P.TOP_PAD + P.ICON + 6 + nameLines * P.NAME_LH + (hasIp ? 4 + P.IP_LH : 0) + 8;

  const boxH = Math.max(portAreaH, leftColH);

  let gx = P.PAD + P.LABEL_W;
  const outGroups = [];
  const anchors = {};
  groups.forEach((g) => {
    // both-sides = single-row layout whose ports expose a TOP and a BOTTOM
    // connection anchor at the device edge (used by passive patch panels to
    // visualise a pass-through). Per edge, the renderer picks the side nearest
    // the connected peer (by vertical position).
    const bothSides = g.layout === 'both-sides';
    const contentX = gx + P.PREFIX_W;
    const cols = Math.max(g.grid.cols, 1);
    const groupW = P.PREFIX_W + cols * P.CELL_W + (cols - 1) * P.GAP_X + 6;
    // Single-row groups may flip their exit side based on the majority peer
    // direction (sideHints); two-row layouts keep the per-row top/bottom split.
    const forcedSide = !bothSides && g.grid.rows === 1 && (sideHints[g.prefix] === 'top' || sideHints[g.prefix] === 'bottom') ? sideHints[g.prefix] : null;
    const cells = g.grid.cells.map((cell) => {
      const x = contentX + cell.c * (P.CELL_W + P.GAP_X);
      const y = cell.r === 0 ? topRowY : bottomRowY;
      const anchorX = x + P.CELL_W / 2;
      if (bothSides) {
        // TOP (device top edge) + BOTTOM (device bottom edge) anchors. Default
        // side is 'top'; both points are exposed via `dual`.
        anchors[cell.port.id] = { x: anchorX, y: 0, side: 'top', dual: true, anchorX, topY: 0, bottomY: boxH, cellX: x, cellY: y };
        return { port: cell.port, x, y, w: P.CELL_W, h: P.CELL_H, side: 'top', dual: true, anchorX, anchorY: 0, topY: 0, bottomY: boxH };
      }
      const side = forcedSide || (cell.r === 0 ? 'top' : 'bottom');
      const anchorY = side === 'top' ? 0 : boxH;
      anchors[cell.port.id] = { x: anchorX, y: anchorY, side, cellX: x, cellY: y };
      return { port: cell.port, x, y, w: P.CELL_W, h: P.CELL_H, side, anchorX, anchorY };
    });
    outGroups.push({ prefix: g.prefix, layout: g.layout, x: gx, contentX, width: groupW, rows: g.grid.rows, cells });
    gx += groupW + P.GROUP_GAP;
  });

  const width = Math.max(gx - P.GROUP_GAP + P.PAD, P.LABEL_W + 96);
  return { width, height: boxH, labelW: P.LABEL_W, topRowY, bottomRowY, nameLines, groups: outGroups, anchors };
}

// For a both-sides (patch panel) port, choose the TOP or BOTTOM anchor based on
// the connected peer's vertical position relative to the port (pass-through:
// peer above -> top, peer below -> bottom).
//   panelPos     — the panel device's absolute position on the page
//   anchor       — anchors[portId] (must be a dual anchor)
//   peerCenterY  — the connected peer's absolute center Y
// Returns 'top' | 'bottom'.
export function pickBothSidesSide(panelPos, anchor, peerCenterY) {
  if (!anchor || !anchor.dual) return anchor?.side || 'top';
  const portCenterAbsY = (panelPos?.y || 0) + (anchor.topY + anchor.bottomY) / 2;
  return peerCenterY <= portCenterAbsY ? 'top' : 'bottom';
}

// Local (unpositioned) anchor point for a chosen both-sides side.
export function bothSidesPoint(anchor, side) {
  if (!anchor) return { x: 0, y: 0 };
  return { x: anchor.anchorX, y: side === 'bottom' ? anchor.bottomY : anchor.topY };
}

// Decide, per device + port-prefix, whether single-row port groups should exit
// UP or DOWN based on the majority direction of their (internal) connections.
// A peer device positioned below this device => 'bottom', otherwise 'top'.
// Returns { [deviceId]: { [prefix]: 'top' | 'bottom' } }.
export function computePanelSideHints(model) {
  const posOf = {};
  const nameOf = {};
  (model.devices || []).forEach((d) => {
    posOf[d.deviceId] = d.position || { x: 0, y: 0 };
    nameOf[d.deviceId] = {};
    (d.ports || []).forEach((p) => (nameOf[d.deviceId][p.id] = p.name));
  });
  const tally = {};
  const add = (deviceId, portId, peerId) => {
    if (!posOf[deviceId] || !posOf[peerId]) return;
    const pname = nameOf[deviceId] ? nameOf[deviceId][portId] : null;
    if (pname == null) return;
    const prefix = parsePortName(pname).prefix;
    const below = posOf[peerId].y > posOf[deviceId].y;
    tally[deviceId] = tally[deviceId] || {};
    tally[deviceId][prefix] = tally[deviceId][prefix] || { top: 0, bottom: 0 };
    if (below) tally[deviceId][prefix].bottom += 1;
    else tally[deviceId][prefix].top += 1;
  };
  (model.internalEdges || []).forEach((e) => {
    add(e.sourceDeviceId, e.sourcePortId, e.targetDeviceId);
    add(e.targetDeviceId, e.targetPortId, e.sourceDeviceId);
  });
  const hints = {};
  Object.keys(tally).forEach((deviceId) => {
    hints[deviceId] = {};
    Object.keys(tally[deviceId]).forEach((prefix) => {
      const tcount = tally[deviceId][prefix];
      hints[deviceId][prefix] = tcount.bottom > tcount.top ? 'bottom' : 'top';
    });
  });
  return hints;
}
